/**
 * Day/night cycle source-of-truth. Owns a uniform buffer + bind group consumed by
 * sky, terrain, buildings, and post passes. The `update(timeSec)` method recomputes
 * sun direction and intensity for the current cycle phase.
 *
 * Phase C: sun-direction starter. Provides a usable sun direction that slowly arcs
 * across the sky; full atmospheric color/ambient curves and the 6-min cycle land in
 * Phase I (just enhance this same module then — same bind-group layout).
 *
 * UBO layout (96 bytes — must match WGSL `DayNight` struct in every consumer):
 *   sunDir:    vec4<f32>   // xyz normalized, w = elevation (sin of altitude angle, -1..1)
 *   sunColor:  vec4<f32>   // xyz radiance, w = ambient intensity
 *   skyParams: vec4<f32>   // x = elevationFrac (0=below, 1=zenith), y..w reserved for sky
 *   time:      vec4<f32>   // x = timeSec, y = cycleFrac (0..1), zw reserved
 *   horizon:   vec4<f32>   // xyz = horizon-tint hint for sky/fog, w reserved
 *   moon:      vec4<f32>   // xyz = moon dir (anti-sun, normalized), w = lunar phase (rad, 0=full → π=new)
 */

import { config } from '../config.ts';
import { createUniformBuffer, writeUniform } from '../gpu/buffers.ts';
import type { Vec3 } from '../util/vec.ts';

export const DAYNIGHT_UBO_SIZE = 96;

export interface DayNightModule {
  readonly buffer: GPUBuffer;
  readonly bindGroupLayout: GPUBindGroupLayout;
  readonly bindGroup: GPUBindGroup;
  /** Most recently computed sun direction (world-space, unit length). Updated by `update()`. */
  readonly sunDir: () => Vec3;
  update(timeSec: number): void;
  destroy(): void;
}

export function createDayNight(device: GPUDevice): DayNightModule {
  const buffer = createUniformBuffer(device, DAYNIGHT_UBO_SIZE, 'daynight');

  const bindGroupLayout = device.createBindGroupLayout({
    label: 'daynight.bgl',
    entries: [{
      binding: 0,
      visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
      buffer: { type: 'uniform', minBindingSize: DAYNIGHT_UBO_SIZE },
    }],
  });

  const bindGroup = device.createBindGroup({
    label: 'daynight.bg',
    layout: bindGroupLayout,
    entries: [{ binding: 0, resource: { buffer } }],
  });

  const scratch = new Float32Array(DAYNIGHT_UBO_SIZE / 4);
  let cachedSunDir: Vec3 = [0, 1, 0];

  // Axial tilt — orbit plane is tilted so the day arc curves visibly in 3D rather
  // than tracing a single vertical line, and sunrise/sunset don't sit on the same chord.
  const tiltRad = (config.axialTiltDeg * Math.PI) / 180;
  const cosTilt = Math.cos(tiltRad);
  const sinTilt = Math.sin(tiltRad);

  const smoothstep = (a: number, b: number, x: number): number => {
    const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
    return t * t * (3 - 2 * t);
  };

  const update = (timeSec: number): void => {
    const cycleFrac = (timeSec / config.dayNightCycleSeconds) % 1;
    const angle = cycleFrac * Math.PI * 2;

    // Sun orbit, phase = 0 → noon (max elevation).
    //   cycleFrac=0    : zenith
    //   cycleFrac=0.25 : west horizon (sunset side)
    //   cycleFrac=0.5  : nadir (midnight)
    //   cycleFrac=0.75 : east horizon (sunrise side)
    const planarX = Math.sin(angle);  // E↔W component
    const planarY = Math.cos(angle);  // up component, peaks at noon

    // Tilt the orbit plane around the X axis: pitches the zenith toward +Z so the
    // arc traces a visible curve and sunrise/sunset are offset in Z relative to noon.
    const sx = planarX;
    const sy = planarY * cosTilt;
    const sz = planarY * sinTilt;
    // (Already unit-length since cos²+sin²=1 and planarX²+planarY²=1.)
    const elevation = sy;
    const elevationFrac = Math.max(0, elevation);

    // Soft terminator — no hard cutoff at horizon; intensity ramps over a few degrees.
    const sunSoft = smoothstep(-0.05, 0.10, elevation);

    // Golden-hour stretch: warmth peaks at horizon and decays as the sun climbs.
    // pow(...) widens the band so the warm look lasts a meaningful slice of the cycle.
    const warmth = Math.pow(1 - elevationFrac, 0.4);

    // Sunrise vs sunset: sunset-side (planarX > 0) leans warm-orange; sunrise leans
    // cool-magenta. Lerp via the signed planarX, gated by warmth so the difference
    // only shows near the horizon.
    const sideBias = Math.max(-1, Math.min(1, planarX * 2));
    const sunsetT = Math.max(0, sideBias);
    const sunriseT = Math.max(0, -sideBias);

    // Base warm sun color (sunset / midday blend).
    const sunR = 1.0;
    const sunG = 1.0 - warmth * (0.30 + 0.05 * sunsetT);
    const sunB = 1.0 - warmth * (0.55 + 0.30 * sunsetT - 0.15 * sunriseT);
    const sunIntensity = sunSoft * 1.35 + 0.03;

    // Ambient: low at midnight (windows still pop), peaks at noon with a wide
    // soft ramp. Night floor lifted from 0.015 → 0.06 so shadowed faces read as
    // material, not silhouette; peak raised so daytime back-faces don't crush.
    const ambient = 0.06 + 0.28 * smoothstep(-0.10, 0.45, elevation);

    // Horizon tint — magenta-cool at sunrise, orange-warm at sunset, neutral mid-day.
    const horizonR = 0.78 - warmth * 0.05 * sunriseT;
    const horizonG = 0.68 - warmth * (0.20 + 0.05 * sunsetT - 0.10 * sunriseT);
    const horizonB = 0.62 - warmth * (0.40 * sunsetT - 0.05 * sunriseT);

    // Moon direction is anti-sun. Lunar phase advances slowly across many cycles so
    // the lit fraction visibly shifts night-to-night.
    const cycleCount = timeSec / config.dayNightCycleSeconds;
    const phase = ((cycleCount * 2 * Math.PI / config.lunarCycleDays) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);

    cachedSunDir = [sx, sy, sz];
    scratch[0] = sx; scratch[1] = sy; scratch[2] = sz; scratch[3] = elevation;
    scratch[4] = sunR * sunIntensity;
    scratch[5] = sunG * sunIntensity;
    scratch[6] = sunB * sunIntensity;
    scratch[7] = ambient;
    scratch[8] = elevationFrac;
    scratch[9] = sunSoft;
    scratch[10] = sunsetT;
    scratch[11] = sunriseT;
    scratch[12] = timeSec;
    scratch[13] = cycleFrac;
    scratch[14] = 0; scratch[15] = 0;
    scratch[16] = horizonR;
    scratch[17] = horizonG;
    scratch[18] = horizonB;
    scratch[19] = 0;
    // moon (bytes 80..95): xyz = anti-sun direction, w = phase
    scratch[20] = -sx;
    scratch[21] = -sy;
    scratch[22] = -sz;
    scratch[23] = phase;
    writeUniform(device, buffer, scratch);
  };

  // Initial write so consumers can bind safely before the first frame.
  update(0);

  return {
    buffer,
    bindGroupLayout,
    bindGroup,
    sunDir: () => cachedSunDir,
    update,
    destroy() {
      buffer.destroy();
    },
  };
}
